import { useQuery } from "@tanstack/react-query";
import { DateRange } from "date-range-picker-mui";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import { useAtom } from "jotai";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { SendJsonMessage } from "react-use-websocket/dist/lib/types";
import {
  callDownloadFileFromPSUrl,
  callGetAllStudies,
  callGetMRQuestions,
  callGetPSUrlForFileDownload,
  callGetQuestionSummary,
  callMRFilters,
  callPMRStudySummary,
} from "../../api/dataSources";
import { FilterResponse } from "../../models/filter";
import { DynamicDateRange } from "./Filter/DateRangeFilter";
import { MarketResearchSocketThreadAtom } from "../../store/market-research/useMarketResearchSocket";
import { downloadAnyFile, extractFileName } from "../../utils/utils";
import useUserContext from "../Authorization/useUserContext";
import {
  MarketResearchDrawerStateAtom,
  MarketResearchStateAtom,
  PMRThinkingStepsCollapsedAtom,
  MarketResearchStudy,
  StudyQuestion,
  TPMRChat,
  TStudySummary,
} from "./models";

dayjs.extend(isSameOrAfter);

const useMarketResearchStore = () => {
  const user = useUserContext();
  const [state, setState] = useAtom(MarketResearchStateAtom);
  const [drawer, setDrawer] = useAtom(MarketResearchDrawerStateAtom);
  const [, setSocketThread] = useAtom(MarketResearchSocketThreadAtom);

  // Added the standalone collapse atom hook
  const [thinkingStepsCollapsed, setThinkingStepsCollapsed] = useAtom(PMRThinkingStepsCollapsedAtom);

  const navigate = useNavigate();

  const list_facets = ["vendor", "therapeutic_area", "product"];

  const search = {
    base_filters: {
      ...state.base_filters,
    },
    ...(state.dateRange?.startDate &&
      state.dateRange?.endDate && {
        dates: {
          start: dayjs(state.dateRange.startDate).format("YYYY-MM-DD"),
          end: dayjs(state.dateRange.endDate).format("YYYY-MM-DD"),
        },
      }),
  };

  // Data: All Studies
  const { data: studies, isLoading: isLoadingStudies } = useQuery<MarketResearchStudy[]>({
    queryKey: ["pmr:all_studies", JSON.stringify(search)],
    queryFn: () => callGetAllStudies(user, search),
  });

  // Data: Study Questions by StudyID
  const { data: studyQuestions, isLoading: isLoadingQuestions } = useQuery<StudyQuestion[]>({
    queryKey: ["pmr:study_questions", state.studyIds, JSON.stringify(search)],
    queryFn: () => callGetMRQuestions(user, state.studyIds.at(0) ?? "", search),
    enabled: state.studyIds.length == 1,
  });

  // TODO: This is the entry point for populating the facet bar but we may need another for the "list" view vs "chat" view
  const { data: facets, isLoading: isLoadingFacets } = useQuery<FilterResponse>({
    queryKey: ["pmr:facets", state.scope, JSON.stringify(state.studyIds)],
    queryFn: () => callMRFilters(user, state.studyIds.at(0)),
  });

  const askQuestion = (
    sendJsonMessage: SendJsonMessage,
    question: string,
    studyIds: string[],
    question_id?: string,
    numOfResponses?: number | undefined,
  ) => {
    // Somewhat hacky way to establish what kind of interaction the chat is:
    // If there is a question_id, assume that a "QnA Tile" was clicked on

    setState((_) => ({
      ..._,
      invocationType: question_id ? "qna" : "user",
      noOfResponses: numOfResponses,
      isLoading: true,
      thinkingSteps: [], // Reset steps for new question
    }));

    // Start collapsed; user can expand manually
    setThinkingStepsCollapsed(true);

    sendJsonMessage({
      action: "streamChat",
      service_requested: "deepchat",
      user_request: {
        query: question,
        search_request: {
          base_filters: { study: studyIds, ...state.base_filters },
        },
        question_id,
      },
    });
  };

  const { data: studySummaryResponse, isLoading: isStudySummaryLoading } = useQuery<TStudySummary>({
    queryKey: ["pmr:study_summary", state.studyIds],
    queryFn: () => callPMRStudySummary(user, state.studyIds.at(0) ?? ""),
    enabled: state.studyIds.length > 0,
  });

  const { data: qaChatResponse, isLoading: isQAChatLoading } = useQuery<TPMRChat>({
    queryKey: ["pmr:study_summary", state.questionId],
    queryFn: () => callGetQuestionSummary(user, state.questionId ?? ""),
    enabled: !!state?.questionId,
  });

  // Toggles a facet selection in the DRAFT layer. Nothing is filtered until
  // applyFilters() commits the draft into base_filters.
  const setFilter = (filterName: string, value: string, isChecked: boolean) => {
    setState((prev) => {
      const currentValues = prev.draft_base_filters[filterName] || [];
      const updatedValues = isChecked
        ? [...currentValues, value] // Add the value if checked
        : currentValues.filter((v) => v !== value); // Remove the value if unchecked

      const _draft_base_filters = { ...prev.draft_base_filters };
      if (updatedValues.length > 0) {
        // If the array has values, update the key
        _draft_base_filters[filterName] = updatedValues;
      } else {
        // If the array is empty, remove the key
        delete _draft_base_filters[filterName];
      }

      return { ...prev, draft_base_filters: _draft_base_filters };
    });
  };

  // Process the data to extract the required arrays
  const { respondentIdsArray, facetsArray } = useMemo(() => {
    if (!facets || !facets.common_filters) {
      return { respondentIds: [], otherFilterValues: [] };
    }
    const respondentIdFilter = facets.common_filters.find((filter) => filter.column_name === "respondent_id");
    const respondentIdsArray = respondentIdFilter ? respondentIdFilter.filter_values : [];
    const facetsArray = facets.common_filters
      .filter((filter) => filter.column_name !== "respondent_id")
      .map((filter) => ({
        filter_name: filter.filter_name,
        column_name: filter.column_name,
        filter_values: filter.filter_values,
      }));
    return { respondentIdsArray, facetsArray };
  }, [facets]);

  const updateRespondentFilterJson = (uncheckedValue: string) => {
    setState((prevState) => {
      // Clone the existing filterJson
      const updatedFilterJson = { ...prevState.base_filters };
      // Ensure base_filters.respondent_id is initialized as a string array or use the default
      const currentRespondentIds: string[] = updatedFilterJson.respondent_id ?? respondentIdsArray ?? [];
      let updatedRespondentIds: string[];

      if (currentRespondentIds.includes(uncheckedValue)) {
        // Remove the unchecked value from respondent_id
        updatedRespondentIds = currentRespondentIds.filter((id: string) => id !== uncheckedValue);
      } else {
        // Add the unchecked value to respondent_id
        updatedRespondentIds = [...currentRespondentIds, uncheckedValue];
      }

      updatedFilterJson.respondent_id = updatedRespondentIds;

      return {
        ...prevState,
        base_filters: updatedFilterJson, // Update filterJson in the state
      };
    });
  };

  // Assumes that the ReactQuery for "pmr:all_studies" is loaded
  const getStudy = (study_key: string) => {
    return studies?.find((study) => study.study_guid == study_key);
  };

  const editQuestion = (question: string) => {
    resetSocket();
    clearChatSession();
    setState((_) => ({ ..._, question }));
    navigate(-1);
  };

  const resetSocket = () => {
    setSocketThread((_) => _ + 1);
  };

  // Clear will retain the selected studyId
  const clearChatSession = () => {
    setState((_state) => ({
      ..._state,
      question: "",
      invocationType: undefined,
      isLoading: false,
      socketStatusMessage: "",
      messages: [],
      thinkingSteps: [], // Clear steps on session clear
    }));
    setThinkingStepsCollapsed(true);
  };

  // TODO: If this is never being called then we should consider removing it
  // Reset will clear everything and reset back to default state
  const resetState = () => {
    setState({
      studyIds: [],
      isLoading: false,
      socketStatusMessage: "",
      messages: [],
      question: "",
      base_filters: {},
      draft_base_filters: {},
      respondentId: "",
      filterCount: 0,
      scope: "list",
      dateRange: null,
      draft_dateRange: null,
      thinkingSteps: [], // Reset steps on full reset
    });
    setThinkingStepsCollapsed(true);

    // Reset Filter Accordion State
    toggleFilterDrawer(false);
    // toggleExpanded([]);
  };

  const toggleFilterDrawer = (open?: boolean) => {
    if (open === undefined) {
      setDrawer((_) => ({ ..._, open: !_.open }));
    } else {
      setDrawer((_) => ({ ..._, open }));
    }
  };

  const toggleExpanded = (sectionKey: string | string[]) => {
    setDrawer((_) => {
      if (Array.isArray(sectionKey)) {
        return { ..._, expanded: sectionKey };
      } else {
        const expanded = _.expanded.includes(sectionKey)
          ? _.expanded.filter((key) => key != sectionKey)
          : [..._.expanded, sectionKey];
        return { ..._, expanded };
      }
    });
  };

  const isExpanded = (sectionKey: string) => {
    return drawer.expanded.includes(sectionKey);
  };

  // Reflects the DRAFT selection so checkboxes respond immediately while the
  // applied results only change on Apply.
  const isFacetChecked = (column_name: string, value: string) => {
    return (state.draft_base_filters[column_name] ?? []).includes(value);
  };

  const filterCount = () => {
    const arrayFilters = Object.entries(search.base_filters)
      .filter(([key]) => key !== "respondent_id")
      // Tricky logic: if is "list" we DO want it to be a key in list_facets, if false we NO NOT want it to be in list_facets
      .filter(([key]) => (state.scope == "list") == list_facets.includes(key))
      .reduce((sum, [, value]) => sum + (Array.isArray(value) ? value.length : 0), 0);

    const dateFilter = search.dates?.start && search.dates?.end ? 1 : 0;
    return arrayFilters + dateFilter;
  };

  const hasFilters = () => {
    return filterCount() > 0;
  };

  // True when the staged draft differs from what is currently applied.
  const hasUnappliedFilterChanges = () => {
    const normalize = (f: Record<string, string[] | undefined>) => {
      const out: Record<string, string[]> = {};
      Object.keys(f)
        .sort()
        .forEach((k) => {
          const v = f[k];
          if (v && v.length > 0) out[k] = [...v].sort();
        });
      return JSON.stringify(out);
    };

    const filtersDiffer = normalize(state.draft_base_filters) !== normalize(state.base_filters);

    const dateKey = (d?: DynamicDateRange | null) =>
      d?.startDate && d?.endDate
        ? `${dayjs(d.startDate).format("YYYY-MM-DD")}:${dayjs(d.endDate).format("YYYY-MM-DD")}`
        : "";
    const datesDiffer = dateKey(state.draft_dateRange) !== dateKey(state.dateRange);

    return filtersDiffer || datesDiffer;
  };

  // Commits the staged draft into the applied filters that drive the queries.
  const applyFilters = () => {
    setState((_state) => ({
      ..._state,
      base_filters: { ..._state.draft_base_filters },
      dateRange: _state.draft_dateRange ?? null,
    }));
  };

  // Seeds the draft from the currently applied filters (used when opening the
  // filter panel so staged state starts from what is applied).
  const syncDraftFromApplied = () => {
    setState((_state) => ({
      ..._state,
      draft_base_filters: { ..._state.base_filters },
      draft_dateRange: _state.dateRange ?? null,
    }));
  };

  const resetFilters = () => {
    setState((_state) => ({
      ..._state,
      base_filters: {},
      draft_base_filters: {},
      dateRange: null,
      draft_dateRange: null,
    }));
  };

  /** Removes any applied facets that are study specific */
  const clearStudyFacetFilters = () => {
    // This logic is intended to be used to clean out study specific facets when transitioning to the All Study view
    setState((_state) => {
      const _cleaned_base_filters: Record<string, string[] | undefined> = {};
      const _cleaned_draft_base_filters: Record<string, string[] | undefined> = {};
      list_facets.forEach((column_name) => {
        if (Object.keys(_state.base_filters).includes(column_name))
          _cleaned_base_filters[column_name] = [...(_state.base_filters[column_name] ?? [])];
        if (Object.keys(_state.draft_base_filters).includes(column_name))
          _cleaned_draft_base_filters[column_name] = [...(_state.draft_base_filters[column_name] ?? [])];
      });

      return {
        ..._state,
        base_filters: _cleaned_base_filters,
        draft_base_filters: _cleaned_draft_base_filters,
      };
    });
  };

  const setScope = (scope: "list" | "chat") => {
    setState((_) => ({ ..._, scope }));
  };

  const toggleLoadingChat = (isLoading?: boolean) => {
    if (isLoading === undefined) {
      setState((_) => ({ ..._, isLoading: !_.isLoading }));
    } else {
      setState((_) => ({ ..._, isLoading }));
    }
  };

  const generateRespondentSummary = (
    sendJsonMessage: SendJsonMessage,
    study_ids: string[],
    respondent_ids?: string[],
  ) => {
    sendJsonMessage({
      action: "streamChat",
      service_requested: "pmr_study_summary",
      user_request: {
        search_request: {
          base_filters: {
            study: study_ids, // state.studyIds,
            respondent_ids: respondent_ids ?? respondentIdsArray, // API Does not support not passing this param
          },
        },
      },
    });
  };

  const resetRespondentsArray = () => {
    setState((prevState) => ({
      ...prevState,
      base_filters: { ...prevState.base_filters, respondent_id: undefined },
    }));
  };

  const safeParseCreatedTs = (raw: string, format: string = "YYYY-MM-DDTHH:mm:ss.SSSSSS"): Date | undefined => {
    const parsed = dayjs(raw, format, true);
    return parsed.isValid() ? parsed.toDate() : undefined;
  };

  const isWithinLast3Weeks = (date: Date, now: Date = new Date()): boolean => {
    const threeWeeksAgo = dayjs(now).subtract(3, "week");
    return dayjs(date).isSameOrAfter(threeWeeksAgo);
  };

  const downloadFile = async (fileGUID: string, s3URL: string) => {
    try {
      const response = await callGetPSUrlForFileDownload(user, fileGUID);
      const blob = await callDownloadFileFromPSUrl(response.data.presigned_url);
      return downloadAnyFile(blob, extractFileName(s3URL));
    } catch (error) {
      return false;
    }
  };

  // Stages the date range in the DRAFT layer; applied on Apply.
  const addDateRangeFilter = (range: DateRange) => {
    setState((_) => {
      return {
        ..._,
        draft_dateRange: range,
      };
    });
  };

  return {
    // -- Store Methods
    askQuestion,
    toggleLoadingChat,
    isLoadingChat: state.isLoading,
    socketStatusMessage: state.socketStatusMessage,
    thinkingSteps: state.thinkingSteps,
    thinkingStepsCollapsed,
    setThinkingStepsCollapsed,
    invocationType: state.invocationType,
    getStudy,
    editQuestion,
    clearChatSession,
    filterCount,
    resetFilterCount: () => setState((_) => ({ ..._, filterCount: 0 })),
    resetSocket,
    safeParseCreatedTs,
    isWithinLast3Weeks,

    // -- Simple State Interactions
    question: state.question,
    setQuestionResponse: (studyResponse?: StudyQuestion) =>
      setState((_) => ({ ..._, questionResponse: studyResponse })),
    setQuestion: (question: string) => setState((_) => ({ ..._, question })),
    setQuestionId: (questionId: string) => setState((_) => ({ ..._, questionId })),
    qaChatResponse,
    isQAChatLoading,
    questionResponse: state.questionResponse,
    numOfResponses: state.noOfResponses,

    studyIds: state.studyIds,
    setSelectedStudyIds: (studyIds?: string[]) => setState((_) => ({ ..._, studyIds: studyIds ?? [] })),

    respondentId: state.respondentId,
    setRespondentId: (respondentId?: string) => setState((_) => ({ ..._, respondentId })),

    messages: state.messages,
    scope: state.scope,
    setScope,

    // -- ReactQuery Managed Variables
    studies,
    isLoadingStudies,

    studyQuestions,
    isLoadingQuestions,

    toggleStudySummary: () => setState((_) => ({ ..._, showStudySummary: !_.showStudySummary })),
    showStudySummary: state.showStudySummary,
    isStudySummaryLoading,
    studySummaryResponse,

    respondentIdsArray,
    facetsArray,
    facets,
    isLoadingFacets,
    search,
    setFilter,
    hasFilters,
    hasUnappliedFilterChanges,
    applyFilters,
    syncDraftFromApplied,
    resetFilters,
    clearStudyFacetFilters,
    updateRespondentFilterJson,

    generateRespondentSummary,
    resetRespondentsArray,

    // -- Drawer State + Methods
    drawer,
    toggleFilterDrawer,
    toggleExpanded,
    isExpanded,
    isFacetChecked,
    //clearInactiveFacets,

    navigate,
    setTherapeuticArea: (therapeuticArea: string) => setState((_) => ({ ..._, therapeuticAreas: therapeuticArea })),
    downloadFile,
    addDateRangeFilter,
    dateRange: state.dateRange,
    draftDateRange: state.draft_dateRange,
  };
};

export default useMarketResearchStore;
